/**
 * BriefLogTable — sortable per-brief log table for the Briefs tab.
 *
 * Replaces the legacy `BriefStatusTable`'s one-row-per-RM aggregation: this
 * shows every individual brief send within the selected window so admins can
 * audit a stretch of sends, spot recurring failures, or just read several
 * briefs for the same RM in sequence.
 *
 * Status chip palette is locked to three values (BLOCKER-06): sent=green,
 * failed=red, skipped=amber. Same chip palette as the trend chart.
 *
 * Click semantics:
 *   • Body click → expand/collapse the row (read the brief inline).
 *   • Username link → navigate to `/user/{userId}` user drilldown.
 * Both Name (`UserLabel`) and User (`@username`) cells wrap their text in
 * a `<Link>` with `stopPropagation` so the link wins over the row toggle.
 *
 * Row key is `userId:date:kind` (date in UTC, YYYY-MM-DD; kind is the raw
 * trace name), so the inline expansion URL param `?expanded=` deep-links exact
 * rows even when the same RM has several briefs — including a morning brief and
 * an onboarding message on the same day — in range.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  SortableTable,
  type SortableColumn,
} from '@/components/SortableTable';
import { UserLabel } from '@/components/ui/user-label';
import { formatRelativeCet } from '@/lib/format-date';
import { formatRating } from '@/lib/format';
import { kindLabel } from '@/lib/trace-labels';
import type { BriefLogRow, BriefStatus } from '@/services/api-types';

// ---------------------------------------------------------------------------
// Status chip — 3 values only (sent/failed/skipped)
// ---------------------------------------------------------------------------

const STATUS_CHIP_CLASS: Record<BriefStatus, string> = {
  sent: 'text-green-700 bg-green-50 border-green-200',
  failed: 'text-red-700 bg-red-50 border-red-200',
  skipped: 'text-amber-700 bg-amber-50 border-amber-200',
};

function StatusChip({ status }: { status: BriefStatus }): React.ReactElement {
  return (
    <span
      data-testid="brief-log-status-chip"
      data-status={status}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium ${STATUS_CHIP_CLASS[status]}`}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelative(iso: string): string {
  return formatRelativeCet(iso);
}


function userHref(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `/user/${encodeURIComponent(userId)}`;
}

function stopRowClick(e: React.MouseEvent | React.KeyboardEvent): void {
  e.stopPropagation();
}

/**
 * Stable per-row key — userId + UTC date + kind. The brief scope predicate now
 * admits BOTH `scheduled_brief` and `onboarding_message`, so a single RM can
 * have a morning brief AND an onboarding message on the same UTC day. The
 * legacy `userId:date` key collided across those two rows (duplicate React
 * keys → expansion/render glitches), so we append `:${kind}` to disambiguate.
 *
 * The kind suffix is appended (rather than switching to `traceId`) because the
 * inline viewer in `BriefsPage` reads this key back via the `?expanded=` URL
 * param and `split(':')`s it to recover `[userId, date]` for the
 * fetch-brief-by-date `BriefMessageViewer` — a traceId key would break that
 * date-grouping persistence. Destructuring `[userId, date]` safely ignores the
 * trailing kind segment.
 */
export function briefLogRowKey(row: BriefLogRow): string {
  const d = new Date(row.sentAt);
  if (Number.isNaN(d.getTime())) return `${row.userId}:invalid:${row.traceId}`;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${row.userId}:${yyyy}-${mm}-${dd}:${row.kind}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BRIEF_LOG_TABLE_ID = 'briefLog';

export interface BriefLogTableProps {
  rows: BriefLogRow[];
  isLoading?: boolean;
  emptyMessage?: string;
  /**
   * Inline row expansion — when BOTH props are set the table forwards them
   * to `<SortableTable>`, which renders a sub-row below each expanded entry
   * containing `renderExpanded(row)`.
   */
  renderExpanded?: (row: BriefLogRow) => React.ReactNode;
  expandedKeys?: ReadonlySet<string>;
  onToggleExpanded?: (row: BriefLogRow) => void;
}

export function BriefLogTable({
  rows,
  isLoading = false,
  emptyMessage = 'No briefs in range',
  renderExpanded,
  expandedKeys,
  onToggleExpanded,
}: BriefLogTableProps): React.ReactElement {
  const columns: Array<SortableColumn<BriefLogRow>> = React.useMemo(
    () => [
      {
        key: 'sentAt',
        header: 'Sent',
        sortable: true,
        format: (v) => formatRelative(v as string),
      },
      {
        key: 'realName',
        header: 'Name',
        format: (_v, row) => {
          const href = userHref(row.userId);
          const label = (
            <UserLabel
              name={row.realName}
              username={row.userId}
              variant="cell"
            />
          );
          return href ? (
            <Link
              to={href}
              onClick={stopRowClick}
              onKeyDown={stopRowClick}
              className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
            >
              {label}
            </Link>
          ) : (
            label
          );
        },
      },
      {
        key: 'userId',
        header: 'User',
        format: (v) => {
          const uname = String(v ?? '');
          const href = userHref(uname);
          const text = `@${uname}`;
          return href ? (
            <Link
              to={href}
              onClick={stopRowClick}
              onKeyDown={stopRowClick}
              className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
            >
              {text}
            </Link>
          ) : (
            <span>{text}</span>
          );
        },
      },
      {
        key: 'kind',
        header: 'Kind',
        sortable: true,
        format: (v) => (
          <span data-testid="brief-log-kind">{kindLabel(v as string)}</span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        format: (_v, row) => <StatusChip status={row.status} />,
      },
      {
        key: 'rating',
        header: 'Rating',
        sortable: true,
        align: 'right',
        format: (v) => formatRating(v as number | null),
      },
    ],
    [],
  );

  return (
    <SortableTable<BriefLogRow>
      tableId={BRIEF_LOG_TABLE_ID}
      columns={columns}
      rows={rows}
      getRowKey={briefLogRowKey}
      defaultSort={{ column: 'sentAt', dir: 'desc' }}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      renderExpanded={renderExpanded}
      expandedKeys={expandedKeys}
      onToggleExpanded={onToggleExpanded}
      mobileCardRender={(row) => {
        const href = userHref(row.userId);
        const label = (
          <UserLabel
            name={row.realName}
            username={row.userId}
            variant="stacked"
            className="min-w-0 flex-1"
          />
        );
        return (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              {href ? (
                <Link
                  to={href}
                  onClick={stopRowClick}
                  onKeyDown={stopRowClick}
                  className="min-w-0 flex-1 text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                >
                  {label}
                </Link>
              ) : (
                label
              )}
              <StatusChip status={row.status} />
            </div>
            <div className="flex items-baseline justify-between gap-3 text-xs">
              <div>
                <span className="text-muted-foreground">Sent: </span>
                <span className="font-medium">{formatRelative(row.sentAt)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Kind: </span>
                <span className="font-medium" data-testid="brief-log-kind">
                  {kindLabel(row.kind)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Rating: </span>
                <span className="font-medium">{formatRating(row.rating)}</span>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}
